import React, { useState, useEffect } from 'react';
import { useProjects } from '../../context/ProjectContext';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Loader2, TrendingUp, BarChart3, Target, Users } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line, Legend } from 'recharts';

const COLORS = ['#6366f1', '#3b82f6', '#06b6d4', '#10b981', '#f59e0b', '#22c55e', '#a855f7', '#ef4444'];

const ProjectAnalytics = () => {
  const { getAnalytics } = useProjects();
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');

  const loadAnalytics = async () => {
    setLoading(true);
    const data = await getAnalytics(fromDate || null, toDate || null);
    setAnalytics(data);
    setLoading(false);
  };

  useEffect(() => {
    const timer = setTimeout(() => loadAnalytics(), 300);
    return () => clearTimeout(timer);
  }, [fromDate, toDate]);

  if (loading) {
    return <div className="flex items-center justify-center h-64"><Loader2 className="w-8 h-8 animate-spin text-blue-600" /></div>;
  }

  if (!analytics) {
    return <div className="text-center py-8 text-slate-500">No analytics data</div>;
  }

  const statusData = Object.entries(analytics.status_counts || {}).map(([name, value]) => ({ name, value }));
  const platformData = Object.entries(analytics.platform_counts || {}).map(([name, value]) => ({ name, value }));
  const funnelData = [
    { stage: 'Total', count: analytics.total },
    { stage: 'Applied', count: analytics.applied },
    { stage: 'Responded', count: analytics.responded },
    { stage: 'Converted', count: analytics.converted }
  ];

  return (
    <div className="space-y-6">
      {/* Date Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex items-end gap-4">
            <div className="space-y-1">
              <Label className="text-sm text-slate-500">From Date</Label>
              <Input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} className="w-[180px]" data-testid="analytics-from-date" />
            </div>
            <div className="space-y-1">
              <Label className="text-sm text-slate-500">To Date</Label>
              <Input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} className="w-[180px]" data-testid="analytics-to-date" />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="border-l-4 border-l-blue-600" data-testid="analytics-kpi-total">
          <CardContent className="p-4">
            <div className="flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-blue-600" />
              <div>
                <p className="text-xs text-slate-500 uppercase">Total Projects</p>
                <p className="text-2xl font-bold">{analytics.total}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-green-600" data-testid="analytics-kpi-converted">
          <CardContent className="p-4">
            <div className="flex items-center gap-2">
              <Target className="w-5 h-5 text-green-600" />
              <div>
                <p className="text-xs text-slate-500 uppercase">Converted</p>
                <p className="text-2xl font-bold text-green-700">{analytics.converted}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-emerald-600" data-testid="analytics-kpi-conversion">
          <CardContent className="p-4">
            <div className="flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-emerald-600" />
              <div>
                <p className="text-xs text-slate-500 uppercase">Conversion Rate</p>
                <p className="text-2xl font-bold text-emerald-700">{analytics.conversion_rate}%</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-cyan-600" data-testid="analytics-kpi-response">
          <CardContent className="p-4">
            <div className="flex items-center gap-2">
              <Users className="w-5 h-5 text-cyan-600" />
              <div>
                <p className="text-xs text-slate-500 uppercase">Response Rate</p>
                <p className="text-2xl font-bold text-cyan-700">{analytics.response_rate}%</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Conversion Funnel */}
        <Card>
          <CardHeader><CardTitle className="text-base">Conversion Funnel</CardTitle></CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={funnelData} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" />
                <YAxis dataKey="stage" type="category" width={80} tick={{ fontSize: 12 }} />
                <Tooltip />
                <Bar dataKey="count" fill="#6366f1" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* By Status */}
        <Card>
          <CardHeader><CardTitle className="text-base">Projects by Status</CardTitle></CardHeader>
          <CardContent>
            {statusData.length > 0 ? (
              <ResponsiveContainer width="100%" height={250}>
                <PieChart>
                  <Pie data={statusData} cx="50%" cy="50%" outerRadius={90} dataKey="value" label={({ name, value }) => `${name}: ${value}`}>
                    {statusData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            ) : <p className="text-center text-slate-400 py-8">No data</p>}
          </CardContent>
        </Card>

        {/* By Platform */}
        <Card>
          <CardHeader><CardTitle className="text-base">Projects by Platform</CardTitle></CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={platformData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                <YAxis />
                <Tooltip />
                <Bar dataKey="value" fill="#3b82f6" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Monthly Trend */}
        <Card>
          <CardHeader><CardTitle className="text-base">Monthly Trends</CardTitle></CardHeader>
          <CardContent>
            {analytics.monthly_trend?.length > 0 ? (
              <ResponsiveContainer width="100%" height={250}>
                <LineChart data={analytics.monthly_trend}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Line type="monotone" dataKey="count" stroke="#6366f1" strokeWidth={2} dot={{ fill: '#6366f1' }} name="Projects" />
                </LineChart>
              </ResponsiveContainer>
            ) : <p className="text-center text-slate-400 py-8">No trend data</p>}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ProjectAnalytics;
